import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../services/authService';
import { resolveActivePlatform, platformSupportsAdInsights } from '../utils/resolveActivePlatform';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Stack,
  Divider,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Warning as ExposuresIcon,
  Insights as InsightsIcon,
  Tune as PreferencesIcon,
  Business as AdvertisersIcon,
  Category as CategoriesIcon,
  Shield as ShieldIcon,
  Recommend as RecommendIcon,
  SmartToy as PrivacyAssistantIcon,
  ChevronLeft as CollapseIcon,
  ChevronRight as ExpandIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { tokens } from '../theme/tokens';

const SIDEBAR_WIDTH = 232;
const SIDEBAR_COLLAPSED = 68;

const PAGES_GROUP = {
  label: 'Pages',
  items: [
    { label: 'Dashboard',       path: '/dashboard',       icon: <DashboardIcon /> },
    { label: 'Exposures',       path: '/exposures',       icon: <ExposuresIcon /> },
    { label: 'Recommendations', path: '/recommendations', icon: <RecommendIcon /> },
    { label: 'Privacy Assistant', path: '/privacy-assistant', icon: <PrivacyAssistantIcon /> },
  ],
};

const AD_DATA_GROUP = {
  label: 'Ad Data',
  items: [
    { label: 'Ad Insights',       path: '/ad-insights',    icon: <InsightsIcon /> },
    { label: 'Ad Preferences',    path: '/ad-preferences', icon: <PreferencesIcon /> },
    { label: 'Advertisers',       path: '/advertisers',    icon: <AdvertisersIcon /> },
    { label: 'Categories',        path: '/categories',     icon: <CategoriesIcon /> },
  ],
};

const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [adNavVisible, setAdNavVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const sync = () => {
      const u = authService.getStoredUser();
      setAdNavVisible(platformSupportsAdInsights(resolveActivePlatform(u)));
    };
    sync();
    authService.getCurrentUser().then((u) => {
      if (u) setAdNavVisible(platformSupportsAdInsights(resolveActivePlatform(u)));
    }).catch(() => {});
  }, [location.pathname]);

  const navGroups = useMemo(
    () => (adNavVisible ? [PAGES_GROUP, AD_DATA_GROUP] : [PAGES_GROUP]),
    [adNavVisible],
  );

  const isActive = (path) => location.pathname === path;

  const width = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH;

  return (
    <Box
      component="nav"
      sx={{
        width,
        minHeight: '100vh',
        flexShrink: 0,
        bgcolor: tokens.bg.sidebar,
        borderRight: `1px solid ${tokens.border.subtle}`,
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.25s ease',
        position: 'sticky',
        top: 0,
        overflow: 'hidden',
      }}
    >
      {/* Logo row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          px: collapsed ? 1 : 2.5,
          py: 2.5,
          borderBottom: `1px solid ${tokens.border.subtle}`,
        }}
      >
        {!collapsed && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                p: 0.75,
                borderRadius: 2,
                background: `linear-gradient(135deg, rgba(24,184,154,0.5) 0%, rgba(59,130,246,0.42) 100%)`,
                display: 'flex',
                border: `1px solid ${tokens.border.subtle}`,
              }}
            >
              <ShieldIcon sx={{ fontSize: 20, color: '#fff' }} />
            </Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, letterSpacing: '-0.02em', color: tokens.text.primary, fontSize: '1.05rem' }}
            >
              SafeSocial
            </Typography>
          </Stack>
        )}
        {collapsed && (
          <Box
            sx={{
              p: 0.75,
              minWidth: 44,
              minHeight: 44,
              borderRadius: 2,
              background: `linear-gradient(135deg, rgba(24,184,154,0.5) 0%, rgba(59,130,246,0.42) 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${tokens.border.subtle}`,
            }}
          >
            <ShieldIcon sx={{ fontSize: 20, color: '#fff' }} />
          </Box>
        )}
        <IconButton
          size="small"
          onClick={() => setCollapsed((c) => !c)}
          sx={{ color: 'rgba(230,237,247,0.5)', ml: collapsed ? 0 : 1, '&:hover': { color: tokens.text.primary } }}
        >
          {collapsed ? <ExpandIcon fontSize="small" /> : <CollapseIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Nav links */}
      <Box sx={{ flex: 1, py: 1.5, overflowY: 'auto', overflowX: 'hidden' }}>
        {navGroups.map((group, gi) => (
          <Box key={group.label}>
            {gi > 0 && (
              <Divider sx={{ borderColor: tokens.border.strong, opacity: 0.55, mx: collapsed ? 1 : 2, my: 1.25 }} />
            )}
            {!collapsed && (
              <Typography
                variant="caption"
                sx={{
                  px: 2.5,
                  pt: gi > 0 ? 0.5 : 0,
                  pb: 1,
                  display: 'block',
                  color: tokens.text.secondary,
                  fontWeight: 700,
                  letterSpacing: '0.11em',
                  textTransform: 'uppercase',
                  fontSize: '0.7rem',
                }}
              >
                {group.label}
              </Typography>
            )}
            {group.items.map((item) => {
              const active = isActive(item.path) && !item.accent;
              const btn = (
                <Box
                  key={`${group.label}-${item.path}-${item.label}`}
                  role="button"
                  tabIndex={0}
                  aria-current={active ? 'page' : undefined}
                  onClick={() => navigate(item.path)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(item.path);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: collapsed ? 0 : 2.5,
                    py: 1.25,
                    minHeight: 44,
                    mx: collapsed ? 0 : 1,
                    borderRadius: collapsed ? 0 : 2,
                    cursor: 'pointer',
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    color: item.accent
                      ? 'rgba(34,211,238,0.88)'
                      : active
                      ? tokens.accent.brandTeal
                      : 'rgba(230,237,247,0.72)',
                    bgcolor: active ? alpha(tokens.accent.brandTeal, 0.14) : 'transparent',
                    borderLeft: active && !collapsed ? `3px solid ${tokens.accent.brandTeal}` : '3px solid transparent',
                    transition: 'background-color 0.18s ease, color 0.18s ease, border-color 0.18s ease',
                    '&:hover': {
                      bgcolor: item.accent ? 'rgba(34,211,238,0.08)' : 'rgba(24,184,154,0.07)',
                      color: tokens.text.primary,
                    },
                    '&:focus-visible': {
                      outline: `2px solid ${tokens.accent.cyan}`,
                      outlineOffset: 2,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', fontSize: 20, flexShrink: 0 }}>{item.icon}</Box>
                  {!collapsed && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: item.accent ? 600 : active ? 700 : 500,
                        fontSize: '0.9rem',
                        fontStyle: item.accent ? 'italic' : 'normal',
                      }}
                    >
                      {item.label}
                    </Typography>
                  )}
                </Box>
              );
              return collapsed ? (
                <Tooltip key={`${group.label}-${item.label}`} title={item.label} placement="right">
                  {btn}
                </Tooltip>
              ) : (
                <React.Fragment key={`${group.label}-${item.label}`}>{btn}</React.Fragment>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default Sidebar;
